import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ReportParameter } from '@app/modules/custom-reports/models/custom-report.model';

interface ReportParameterControl extends ReportParameter {
  controlName: string;
}

@Component({
  selector: 'app-report-parameters-dialog',
  templateUrl: './report-parameters-dialog.component.html',
  styleUrls: ['./report-parameters-dialog.component.scss']
})
export class ReportParametersDialogComponent implements OnInit {
  public reportName: string;
  public parameters: ReportParameterControl[];
  public form: UntypedFormGroup;

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<ReportParametersDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: { reportName: string, parameters: ReportParameter[] }
  ) {
    this.reportName = data.reportName;
    this.parameters = data.parameters.map((parameter, index) => ({
      ...parameter,
      controlName: `param_${index}`
    }));
  }

  ngOnInit(): void {
    const controls = this.parameters.reduce((group, parameter) => {
      group[parameter.controlName] = [
        this.defaultControlValue(parameter),
        parameter.allowNull ? [] : [Validators.required]
      ];
      return group;
    }, {} as Record<string, any>);

    this.form = this.fb.group(controls);
  }

  private defaultControlValue(parameter: ReportParameterControl): any {
    if (parameter.defaultValue === undefined || parameter.defaultValue === null) {
      return parameter.type === 'Boolean' ? false : '';
    }

    if (parameter.type === 'Boolean') {
      return parameter.defaultValue.toLowerCase() === 'true';
    }

    if (parameter.type === 'DateTime') {
      const date = new Date(parameter.defaultValue);
      return isNaN(date.getTime()) ? '' : date;
    }

    return parameter.defaultValue;
  }

  close(): void {
    this.dialogRef.close();
  }

  run(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const values: Record<string, string> = {};

    this.parameters.forEach(parameter => {
      values[parameter.name] = this.formatValue(parameter, this.form.value[parameter.controlName]);
    });

    this.dialogRef.close(values);
  }

  private formatValue(parameter: ReportParameterControl, value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (Array.isArray(value)) {
      return value.join(',');
    }

    // mat-datepicker throughout this app works through MomentUtcDateAdapter (see
    // app.module.ts), so a DateTime parameter's control value is a Moment instance, not a
    // native Date - `instanceof Date` misses it, falling through to String(value), which for
    // a Moment mimics Date.prototype.toString() (e.g. "Fri Sep 04 2026 00:00:00 GMT+0000") -
    // a format the backend's date parsing rejects. Duck-type on toISOString instead, since
    // both Date and Moment implement it.
    if (typeof value?.toISOString === 'function') {
      return value.toISOString();
    }

    return String(value);
  }
}
